import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyYenIcon,
  MapPinIcon,
  UsersIcon,
  PlayIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

// API base already points to "/api" per shared config
const API = API_BASE;

const JobManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reposting, setReposting] = useState({});
  const MAX_JOBS_ALLOWED = 3;

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      navigate('/');
      return;
    }
    fetchMyJobs();
  }, [user, navigate]);

  const fetchMyJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs/my-jobs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // 3件分のスロットを準備（アクティブ求人 + 空きスロット）
      const activeJobs = response.data.filter(job => job.status === 'active');
      const inactiveJobs = response.data.filter(job => job.status !== 'active');
      
      // 最大3件まで表示するスロット配列を作成
      const jobSlots = [];
      
      // アクティブ求人を追加
      for (let i = 0; i < Math.min(activeJobs.length, MAX_JOBS_ALLOWED); i++) {
        jobSlots.push(activeJobs[i]);
      }
      
      // 空きスロットを追加
      for (let i = activeJobs.length; i < MAX_JOBS_ALLOWED; i++) {
        jobSlots.push(null);
      }
      
      setJobs(jobSlots);
      
      // 過去の求人履歴も保存（再投稿用）
      setJobHistory(inactiveJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('求人情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const [jobHistory, setJobHistory] = useState([]);

  const handleRepost = async (job) => {
    try {
      setReposting(prev => ({ ...prev, [job.id]: true }));
      
      // 再投稿用のデータを準備
      const repostData = {
        ...job,
        publication_start_date: new Date().toISOString().split('T')[0],
        publication_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30日後
        status: 'active'
      };
      
      delete repostData.id;
      delete repostData.created_at;
      delete repostData.updated_at;
      
      const response = await axios.post(`${API}/jobs`, repostData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('求人を再投稿しました！');
      fetchMyJobs(); // リロード
    } catch (error) {
      console.error('Error reposting job:', error);
      toast.error('再投稿に失敗しました');
    } finally {
      setReposting(prev => ({ ...prev, [job.id]: false }));
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('この求人を削除しますか？')) return;
    
    try {
      await axios.delete(`${API}/jobs/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('求人を削除しました');
      fetchMyJobs(); // リロード
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('削除に失敗しました');
    }
  };

  const isExpired = (job) => {
    if (!job?.publication_end_date) return false;
    return new Date(job.publication_end_date) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  const renderJobSlot = (job, index) => {
    if (!job) {
      // 空きスロット
      return (
        <div key={`empty-${index}`} className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <PlusIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">求人スロット {index + 1}</h3>
            <p className="mt-1 text-sm text-gray-500">新しい求人を投稿できます</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/create-job')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                求人を投稿
              </button>
            </div>
          </div>
        </div>
      );
    }

    const expired = isExpired(job);

    return (
      <div key={job.id} className={`bg-white rounded-lg shadow-sm border overflow-hidden ${expired ? 'border-red-200' : 'border-gray-200'}`}>
        {/* サムネイル画像エリア */}
        <div className="relative h-48 bg-gray-200">
          {job.images && job.images.length > 0 ? (
            <img
              src={`data:image/jpeg;base64,${job.images[0]}`}
              alt={job.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
              <UsersIcon className="h-20 w-20 text-gray-500" />
            </div>
          )}
          
          {/* 掲載終了帯 */}
          {expired && (
            <div className="absolute top-0 left-0 transform rotate-12 translate-x-4 translate-y-4">
              <div className="bg-red-600 text-white px-8 py-1 text-sm font-bold transform -rotate-45 shadow-lg">
                掲載終了
              </div>
            </div>
          )}

          {/* 動画インジケーター */}
          {job.videos && job.videos.length > 0 && (
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs flex items-center">
              <PlayIcon className="w-3 h-3 mr-1" />
              動画あり
            </div>
          )}
        </div>

        {/* 求人情報 */}
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{job.title}</h3>
          
          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center">
              <CurrencyYenIcon className="w-4 h-4 mr-2" />
              <span>時給 {job.hourly_rate?.toLocaleString()}</span>
            </div>
            <div className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-2" />
              <span>{job.work_hours || `${job.duration_hours}時間`}</span>
            </div>
            <div className="flex items-center">
              <MapPinIcon className="w-4 h-4 mr-2" />
              <span className="truncate">{job.location}</span>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <span>掲載期間: {formatDate(job.publication_start_date)} 〜 {formatDate(job.publication_end_date)}</span>
            </div>
          </div>

          {/* ステータス表示 */}
          <div className="mb-4">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              expired ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}>
              {expired ? '掲載終了' : '掲載中'}
            </span>
          </div>

          {/* アクションボタン */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/jobs/${job.id}`)}
              className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <EyeIcon className="w-4 h-4 mr-1" />
              詳細
            </button>
            
            {!expired && (
              <button
                onClick={() => navigate(`/edit-job/${job.id}`)}
                className="flex items-center px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                <PencilIcon className="w-4 h-4 mr-1" />
                編集
              </button>
            )}
            
            <button
              onClick={() => handleRepost(job)}
              disabled={reposting[job.id]}
              className="flex items-center px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-green-800 rounded-md transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-1 ${reposting[job.id] ? 'animate-spin' : ''}`} />
              再投稿
            </button>
            
            <button
              onClick={() => handleDelete(job.id)}
              className="flex items-center px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-800 rounded-md transition-colors"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              削除
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">求人管理</h1>
            <p className="mt-2 text-gray-600">最大3件まで同時に求人を投稿できます</p>
          </div>
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center px-4 py-2 text-gray-600 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>

        {/* 求人スロット（3件分） */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {jobs.map((job, index) => renderJobSlot(job, index))}
        </div>

        {/* 求人履歴 */}
        {jobHistory.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">求人履歴</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobHistory.map((job) => (
                <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden opacity-75">
                  {/* 履歴の求人カード（簡略版） */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{job.title}</h3>
                    <div className="space-y-1 text-sm text-gray-600 mb-4">
                      <div>時給 {job.hourly_rate?.toLocaleString()}</div>
                      <div>投稿日: {formatDate(job.created_at)}</div>
                      <div>掲載期間: {formatDate(job.publication_start_date)} 〜 {formatDate(job.publication_end_date)}</div>
                    </div>
                    <button
                      onClick={() => handleRepost(job)}
                      disabled={reposting[job.id]}
                      className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      <ArrowPathIcon className={`w-4 h-4 mr-2 ${reposting[job.id] ? 'animate-spin' : ''}`} />
                      この内容で再投稿
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobManagementPage;